import React, { useEffect, useMemo, useState } from "react";
import "./AfricanVillage.css";
import { normalizeBusinessPhotos } from "./BusinessPhotoGallery";

const configuredApiBase =
  typeof process !== "undefined" && process.env
    ? process.env.REACT_APP_GOOURHUB_BUSINESS_API
    : "";

const API_BASE_URL = (
  configuredApiBase || "https://business-api.goourhub.com"
).replace(/\/+$/, "");

// This is the approved African Village record currently stored in GoOurHub.
const AFRICAN_VILLAGE_BUSINESS_ID =
  "363314f4-7e5a-496f-81cb-ee2da4a7550f";

const FALLBACK_HERO = require("../images/AV1.jpg");

const fallbackGallery = [1, 2, 3, 4, 5, 6, 7].map((num) =>
  require(`../images/AV${num}.jpg`)
);

function AfricanVillage() {
  const [business, setBusiness] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    const loadBusiness = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/businesses/${encodeURIComponent(
            AFRICAN_VILLAGE_BUSINESS_ID
          )}`,
          {
            method: "GET",
            headers: { Accept: "application/json" },
            signal: controller.signal,
          }
        );

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          return;
        }

        const record = payload?.business || payload;

        if (!controller.signal.aborted && record && typeof record === "object") {
          setBusiness(record);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn(
            "Unable to load African Village managed photos. Using local gallery.",
            error
          );
        }
      }
    };

    loadBusiness();

    return () => controller.abort();
  }, []);

  const managedPhotoData = useMemo(() => {
    if (!business) {
      return {
        photos: [],
        coverPhoto: "",
      };
    }

    const details =
      business.details &&
      typeof business.details === "object" &&
      !Array.isArray(business.details)
        ? business.details
        : {};

    return normalizeBusinessPhotos(business, details);
  }, [business]);

  const heroImage = managedPhotoData.coverPhoto || FALLBACK_HERO;

  // Once managed photos exist, those become the public gallery.
  // Old local AV photos remain the fallback so the page never looks empty.
  const galleryPhotos =
    managedPhotoData.photos.length > 0
      ? managedPhotoData.photos
      : fallbackGallery;

  return (
    <div className="african-village">
      <header
        className="hero"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="hero-content solid-bg">
          <h1 className="hero-title">
            Mention GoOurHub To Get 10% Off
          </h1>

          <p className="hero-subtitle">
            Experience the rich and diverse taste of African cuisine.
          </p>

          <a
            href="https://www.google.com/maps/dir//10918+Wurzbach+Rd+%23131,+San+Antonio,+TX+78230/@29.5408617,-98.6338819,12z/data=!4m8!4m7!1m0!1m5!1m1!1s0x865c613645d036cf:0xb773c3c2c7367d14!2m2!1d-98.5514944!2d29.5408799?hl=en-us&entry=ttu&g_ep=EgoyMDI1MDQyMC4wIKXMDSoASAFQAw%3D%3D"
            target="_blank"
            rel="noopener noreferrer"
          >
            <button className="order-button">
              Visit Today
            </button>
          </a>
        </div>
      </header>

      <section className="about">
        <h2>Welcome to African Village</h2>

        <p>
          Classic Ethiopian/Eritrean dishes like kitfo, tibs &amp; injera,
          plus veggie options, served with a warm and inviting atmosphere.
        </p>

        <p>Phone : 210-467-5102</p>

        <p className="call-to-order">
          Call: 210-467-5102 to place your order.
          <br />
          <br />
          <span className="discount-note">
            Mention GoOurHub and get 10% off!
          </span>
        </p>

        <a
          href="https://places.singleplatform.com/african-village/menu#menu_3052178"
          target="_blank"
          rel="noopener noreferrer"
          className="menu-link"
        >
          View Our Menu
        </a>
      </section>

      <section className="address">
        <h3>Find Us Here</h3>

        <p className="highlighted-text">
          <a
            href="https://g.co/kgs/xYMnBfr"
            target="_blank"
            rel="noopener noreferrer"
            className="map-link"
          >
            Wurzbach Plaza | 10918 Wurzbach Rd #131, San Antonio, TX 78230
          </a>
        </p>
      </section>

      <section className="features">
        <div className="feature-box slim-box">
          <h4>Features</h4>
          <ul>
            <li>
              Serves Happy Hour Food | Serves Vegan Dishes | High Chairs
              Available
            </li>
          </ul>
        </div>
      </section>

      <section className="gallery">
        {galleryPhotos.map((photo, index) => (
          <div
            key={`${photo}-${index}`}
            className="gallery-item"
          >
            <img
              src={photo}
              alt={`African Village photo ${index + 1}`}
              className="gallery-img"
              loading="lazy"
            />
          </div>
        ))}
      </section>
    </div>
  );
}

export default AfricanVillage;
